'use client';

import { forwardRef, useState } from 'react';

import { Input, type InputProps } from '@eliadmin/core/src/components/ui/input';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type PasswordInputProps = Omit<InputProps, 'type' | 'after' | 'before'> & {
  showLabel?: string;
  hideLabel?: string;
};

/**
 * Figma „Input“ with the „Zobraziť“ action: a text button in text/brand (DLZ/Button/M) inside the
 * field toggles the visibility; it reads „Skryť“ while the password is shown. The right inset is the
 * 16 px of the core `Input` action slot (Figma 16–18 px) – no override of it, so a stale style sheet
 * cannot drop the padding.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ showLabel = 'Zobraziť', hideLabel = 'Skryť', className, ...props }, ref) => {
    const [visible, setVisible] = useState(false);

    return (
      <Input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={{ ...className, main: cn('pr-24', className?.main) }}
        after={
          <button
            type="button"
            aria-pressed={visible}
            onClick={() => setVisible(current => !current)}
            className="dlz-button-m rounded-sm text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]"
          >
            {visible ? hideLabel : showLabel}
          </button>
        }
        {...props}
      />
    );
  },
);
PasswordInput.displayName = 'PasswordInput';
